import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  useApiAdminwidgetsGetCollection,
  useApiWidgetWorkspacesGetCollection,
  apiAdminwidgetWorkspacesIdPatch,
  apiAdminwidgetWorkspacesPost,
  apiMediasPost,
  getApiMediasIdGetQueryKey,
  getApiWidgetWorkspacesGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type {
  WidgetWidgetReadModel,
  WidgetWorkspaceWidgetWorkspaceReadModel
} from '@r/api/generated/client.schemas.ts';
import { useUploadFile } from '@r/hooks/use-upload-file.ts';
import {
  getMediaIdFromIri,
  MAX_WIDGET_SLIDES,
  type ContentBlockPageId,
  isWidgetKeyForContentBlockPage
} from '@r/helpers/widget-workspace-slides.ts';
import {
  CONTENT_BLOCK_PAGES,
  filterBlocksForPage
} from '@admin/pages/personalization/content-block-pages.ts';
import { getNextSortOrderForPage } from '@admin/pages/personalization/content-block-hub-order.ts';
import {
  buildPatchConfigurations,
  createSlidesSnapshot,
  getEditorPreviewWidgetSlides,
  getDefaultSlideValues,
  getWidgetItemDefinitionIri,
  isMediaFormValueFilled,
  isMediaItemDefinition,
  isSlideFieldRequired,
  isSlideFormValueFilled,
  findMissingMediaIriInPatch,
  getRemovedSlideIndexes,
  loadSlidesFromWorkspace,
  normalizeSlideSelectValues,
  resolveSelectFieldValue,
  reindexSlidesAfterRemoval,
  type ItemDefinition,
  type SlideFormValues
} from '@admin/pages/personalization/content-block-slides.ts';
import {
  contentBlockEditorReducer,
  initialContentBlockEditorState
} from '@admin/pages/personalization/content-block-editor-state.ts';
import {
  createSchedulingSnapshot,
  emptyWorkspaceSchedule,
  isScheduleRangeValid,
  scheduleFromWorkspace,
  toApiSchedulePayload,
  type WorkspaceScheduleState
} from '@admin/pages/personalization/content-block-workspace-schedule.ts';
import { toast } from 'sonner';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

/** Normalise une couleur HSL saisie (avec ou sans wrapper `hsl()`) pour l’API. */
const cleanHSLString = (hslString: string): string => {
  const trimmedString = hslString.trim();
  const hslRegex = /^(hsla?)\(([^)]+)\)$/i;
  const match = hslRegex.exec(trimmedString);

  if (match?.[2]) {
    const content = match[2];
    return content.replace(/,\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }

  return trimmedString.replace(/,\s*/g, ' ').replace(/;\s*$/, '').trim();
};

/**
 * Hook central de l’éditeur de bloc :
 * - charge widgets + workspaces, construit le schéma Zod depuis `itemDefinitions` ;
 * - gère multi-slides (reducer), upload médias → IRI, PATCH/POST workspace ;
 * - planification, aperçu live, garde « modifications non enregistrées ».
 */
export type ContentBlockEditorProps =
  | { mode: 'edit'; workspaceId: number }
  | { mode: 'create'; pageId: ContentBlockPageId; widgetId: number };

export type ContentBlockEditorLoadState = 'loading' | 'error' | 'not_found' | 'ready';

export type ContentBlockEditorMode = ContentBlockEditorProps['mode'];

export interface ContentBlockEditorUploadState {
  isUploading: boolean;
  isMediaUploading: boolean;
}

export interface ContentBlockEditorDialogFlags {
  isUnsavedWarningOpen: boolean;
  isSlideRemovalSaving: boolean;
}

/** État et actions de l’éditeur de bloc widget. */
export function useContentBlockEditor(props: ContentBlockEditorProps) {
  const { mode } = props;
  const createPageId = mode === 'create' ? props.pageId : undefined;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showOrvalErrorToast = useOrvalErrorToast();
  const isCreatingBlock = mode === 'create';
  const pendingWidgetId = mode === 'create' ? props.widgetId : null;
  const editorWorkspaceId = mode === 'edit' ? props.workspaceId : null;
  const [editorState, dispatchEditor] = useReducer(
    contentBlockEditorReducer,
    initialContentBlockEditorState
  );
  const {
    activeSlide,
    slideCount,
    slidesData,
    savedSlidesSnapshot,
    schedule,
    savedSchedulingSnapshot
  } = editorState;
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [isUnsavedWarningOpen, setIsUnsavedWarningOpen] = useState(false);
  const [pendingSlideRemoval, setPendingSlideRemoval] = useState<number | null>(null);
  const [isSlideRemovalSaving, setIsSlideRemovalSaving] = useState(false);
  const skipUnsavedWarningRef = useRef(false);
  const pendingLeaveActionRef = useRef<(() => void) | null>(null);
  const hasUnsavedChangesRef = useRef(false);
  const queryClient = useQueryClient();

  const widgetsQuery = useApiAdminwidgetsGetCollection();
  useOrvalQueryErrorToast(widgetsQuery, t('admin.contentblock.error'));

  const widgetWorkspacesQuery = useApiWidgetWorkspacesGetCollection({
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false
    }
  });

  useEffect(() => {
    if (widgetWorkspacesQuery.isError) {
      showOrvalErrorToast(
        widgetWorkspacesQuery.error,
        t('admin.contentblock.fetch.widgetworkspaceserror')
      );
    }
  }, [showOrvalErrorToast, widgetWorkspacesQuery.error, widgetWorkspacesQuery.isError, t]);

  const widgets = widgetsQuery.data;
  const widgetWorkspaces = widgetWorkspacesQuery.data;

  const selectedWorkspace = useMemo(() => {
    if (editorWorkspaceId === null) return undefined;
    return widgetWorkspaces?.find((workspace) => workspace.id === editorWorkspaceId);
  }, [editorWorkspaceId, widgetWorkspaces]);

  const selectedPageId = useMemo((): ContentBlockPageId => {
    if (mode === 'create' && createPageId) {
      return createPageId;
    }

    const widgetKey = selectedWorkspace?.widget?.key;
    if (!widgetKey) return 'home';

    for (const page of CONTENT_BLOCK_PAGES) {
      if (isWidgetKeyForContentBlockPage(widgetKey, page.id)) {
        return page.id;
      }
    }

    return 'home';
  }, [mode, createPageId, selectedWorkspace?.widget?.key]);

  const pageBlocks = useMemo(
    () => filterBlocksForPage(widgetWorkspaces, selectedPageId),
    [widgetWorkspaces, selectedPageId]
  );

  const currentWidget: WidgetWidgetReadModel | undefined = useMemo(() => {
    if (selectedWorkspace) {
      return widgets?.find((widget) => widget.id === selectedWorkspace.widget.id);
    }
    if (pendingWidgetId !== null) {
      return widgets?.find((widget) => widget.id === pendingWidgetId);
    }
    return widgets?.[0];
  }, [widgets, selectedWorkspace, pendingWidgetId]);

  const itemDefinitions = useMemo<ItemDefinition[]>(
    () =>
      currentWidget?.itemDefinitions?.map((itemDef, index) => ({
        ...itemDef,
        sortOrder: index,
        maxLength: itemDef.maxLength === null ? undefined : itemDef.maxLength,
        defaultValue: itemDef.defaultValue === null ? undefined : itemDef.defaultValue,
        options: itemDef.options === null ? undefined : itemDef.options
      })) ?? [],
    [currentWidget]
  );

  const { progresses, isUploading } = useUploadFile();

  /** Construit le schéma Zod du formulaire à partir des définitions de champs du widget. */
  const createFormSchema = (itemDefinitions: ItemDefinition[]) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {};

    for (const itemDef of itemDefinitions) {
      let fieldSchema: z.ZodTypeAny;

      switch (itemDef.itemType) {
        case 'url':
          fieldSchema = z
            .string()
            .optional()
            .superRefine((val, ctx) => {
              const customIssueCode = z.ZodIssueCode.custom;
              const invalidUrlMessage = t('admin.contentblock.form.validation.url.invalid');
              const invalidDomainMessage = t('admin.contentblock.form.validation.url.domain');

              if (!val || val.trim() === '') return;

              try {
                z.string().url().parse(val);
              } catch {
                ctx.addIssue({
                  code: customIssueCode,
                  message: invalidUrlMessage
                });
                return;
              }

              try {
                const url = new URL(val);
                if (!url.hostname.includes('.') || url.hostname.split('.').length < 2) {
                  ctx.addIssue({
                    code: customIssueCode,
                    message: invalidDomainMessage
                  });
                }
              } catch {
                ctx.addIssue({
                  code: customIssueCode,
                  message: invalidUrlMessage
                });
              }
            });
          break;
        case 'hsl_color':
          fieldSchema = z.string().optional();
          break;
        case 'media':
          fieldSchema = z.union([z.string(), z.array(z.instanceof(File))]).optional();
          break;
        case 'select': {
          const selectOptions = itemDef.options?.filter((option) => option.length > 0) ?? [];
          const selectFallback = resolveSelectFieldValue(itemDef, itemDef.defaultValue);
          if (selectOptions.length >= 1) {
            fieldSchema = z.preprocess(
              (value) => resolveSelectFieldValue(itemDef, value ?? selectFallback),
              z.enum(selectOptions as [string, ...string[]])
            );
          } else {
            fieldSchema = z.string().optional();
          }
          break;
        }
        case 'filter_intensity':
          fieldSchema = z
            .string()
            .refine((val) => {
              if (!val) return true;
              const num = Number.parseInt(val, 10);
              return !Number.isNaN(num) && num >= 0 && num <= 100;
            }, t('admin.contentblock.form.validation.filterintensity.range'))
            .optional();
          break;
        default:
          fieldSchema = z.string().optional();
      }

      if (isSlideFieldRequired(itemDef)) {
        fieldSchema = fieldSchema.refine((val) => isSlideFormValueFilled(itemDef, val), {
          message: t('admin.contentblock.form.validation.required', { field: itemDef.itemLabel })
        });
      }

      schemaFields[itemDef.itemKey] = fieldSchema;
    }

    return z.object(schemaFields);
  };

  const formSchema = itemDefinitions.length > 0 ? createFormSchema(itemDefinitions) : z.object({});

  const form = useForm<SlideFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultSlideValues(itemDefinitions),
    mode: 'onChange'
  });

  const watchedSlideValues = form.watch();

  const currentSlidesSnapshot = useMemo(
    () =>
      createSlidesSnapshot(
        slideCount,
        slidesData,
        activeSlide,
        watchedSlideValues,
        itemDefinitions
      ),
    [activeSlide, itemDefinitions, slideCount, slidesData, watchedSlideValues]
  );

  const previewWidgetSlides = useMemo(
    () =>
      itemDefinitions.length === 0
        ? []
        : getEditorPreviewWidgetSlides(
            slideCount,
            slidesData,
            activeSlide,
            watchedSlideValues,
            itemDefinitions
          ),
    [activeSlide, itemDefinitions, slideCount, slidesData, watchedSlideValues]
  );

  const currentSchedulingSnapshot = useMemo(() => createSchedulingSnapshot(schedule), [schedule]);

  const hasUnsavedSlideChanges =
    savedSlidesSnapshot !== null && currentSlidesSnapshot !== savedSlidesSnapshot;
  const hasUnsavedSchedulingChanges =
    savedSchedulingSnapshot !== null && currentSchedulingSnapshot !== savedSchedulingSnapshot;
  const hasUnsavedChanges = hasUnsavedSlideChanges || hasUnsavedSchedulingChanges;

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const requestUnsavedChangesConfirmation = useCallback(
    (leaveAction: () => void) => {
      if (!hasUnsavedChanges || skipUnsavedWarningRef.current) {
        leaveAction();
        return;
      }

      pendingLeaveActionRef.current = leaveAction;
      setIsUnsavedWarningOpen(true);
    },
    [hasUnsavedChanges]
  );

  const handleConfirmLeave = () => {
    skipUnsavedWarningRef.current = true;
    setIsUnsavedWarningOpen(false);
    const leaveAction = pendingLeaveActionRef.current;
    pendingLeaveActionRef.current = null;
    leaveAction?.();
    window.setTimeout(() => {
      skipUnsavedWarningRef.current = false;
    }, 0);
  };

  const handleUnsavedWarningOpenChange = (open: boolean) => {
    setIsUnsavedWarningOpen(open);

    if (!open) {
      pendingLeaveActionRef.current = null;
    }
  };

  const handleSlideRemovalOpenChange = (open: boolean) => {
    if (isSlideRemovalSaving) return;

    if (!open) {
      setPendingSlideRemoval(null);
    }
  };

  const preventDialogDismissWhileSaving = (event: { preventDefault: () => void }) => {
    if (isSlideRemovalSaving) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChangesRef.current || skipUnsavedWarningRef.current) return;

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const confirmUnsavedBeforeLeave = useEffectEvent((leaveAction: () => void) => {
    requestUnsavedChangesConfirmation(leaveAction);
  });

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (
        !hasUnsavedChangesRef.current ||
        skipUnsavedWarningRef.current ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest<HTMLAnchorElement>('a[href]');
      if (!anchor || anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;

      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      if (nextUrl.origin !== currentUrl.origin) return;
      if (nextUrl.pathname === currentUrl.pathname && nextUrl.search === currentUrl.search) return;

      event.preventDefault();
      event.stopPropagation();

      const destination = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      confirmUnsavedBeforeLeave(() => {
        void navigate({ to: destination });
      });
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [navigate]);

  /** Réinitialise l’éditeur pour un nouveau bloc (1 slide vide, sans données workspace). */
  const resetEditorForNewBlock = useCallback(
    (widgetId: number) => {
      const widget = widgets?.find((item) => item.id === widgetId);
      if (!widget?.itemDefinitions) return;

      const definitions = widget.itemDefinitions.map((itemDef, index) => ({
        ...itemDef,
        sortOrder: index,
        maxLength: itemDef.maxLength === null ? undefined : itemDef.maxLength,
        defaultValue: itemDef.defaultValue === null ? undefined : itemDef.defaultValue,
        options: itemDef.options === null ? undefined : itemDef.options
      }));

      const defaultSlides = { 1: getDefaultSlideValues(definitions) };
      const initialSchedule = emptyWorkspaceSchedule();
      form.reset(defaultSlides[1]);
      dispatchEditor({
        type: 'replace',
        state: {
          activeSlide: 1,
          slideCount: 1,
          slidesData: defaultSlides,
          savedSlidesSnapshot: createSlidesSnapshot(
            1,
            defaultSlides,
            1,
            defaultSlides[1],
            definitions
          ),
          schedule: initialSchedule,
          savedSchedulingSnapshot: createSchedulingSnapshot(initialSchedule)
        }
      });
    },
    [form, widgets]
  );

  const handleBackToHub = () => {
    requestUnsavedChangesConfirmation(() => {
      void navigate({ to: '/content/content-block' });
    });
  };

  useEffect(() => {
    if (!isCreatingBlock || pendingWidgetId === null) return;

    resetEditorForNewBlock(pendingWidgetId);
  }, [isCreatingBlock, pendingWidgetId, resetEditorForNewBlock]);

  useEffect(() => {
    if (itemDefinitions.length === 0 || isCreatingBlock) return;

    if (!selectedWorkspace) return;

    const loadedSlides = loadSlidesFromWorkspace(selectedWorkspace, itemDefinitions);
    const firstSlideValues = loadedSlides.slidesData[1] ?? getDefaultSlideValues(itemDefinitions);
    const loadedSchedule = scheduleFromWorkspace(selectedWorkspace);
    form.reset(firstSlideValues);
    dispatchEditor({
      type: 'replace',
      state: {
        activeSlide: 1,
        slideCount: loadedSlides.slideCount,
        slidesData: loadedSlides.slidesData,
        savedSlidesSnapshot: createSlidesSnapshot(
          loadedSlides.slideCount,
          loadedSlides.slidesData,
          1,
          firstSlideValues,
          itemDefinitions
        ),
        schedule: loadedSchedule,
        savedSchedulingSnapshot: createSchedulingSnapshot(loadedSchedule)
      }
    });
  }, [selectedWorkspace, itemDefinitions, isCreatingBlock, form.reset]);

  const handleScheduleChange = useCallback((nextSchedule: WorkspaceScheduleState) => {
    dispatchEditor({ type: 'patch', patch: { schedule: nextSchedule } });
  }, []);

  /** Sauvegarde la slide courante en mémoire et affiche les valeurs de la slide cible. */
  const handleSlideChange = (slideIndex: number) => {
    const currentValues = normalizeSlideSelectValues(form.getValues(), itemDefinitions);
    const updatedSlidesData = { ...slidesData, [activeSlide]: currentValues };

    dispatchEditor({
      type: 'patch',
      patch: { slidesData: updatedSlidesData, activeSlide: slideIndex }
    });
    form.reset(updatedSlidesData[slideIndex] ?? getDefaultSlideValues(itemDefinitions));
  };

  /** Ajoute une slide (max 3) avec des champs par défaut et la sélectionne. */
  const handleAddSlide = () => {
    if (slideCount >= MAX_WIDGET_SLIDES || itemDefinitions.length === 0) return;

    const currentValues = normalizeSlideSelectValues(form.getValues(), itemDefinitions);
    const updatedSlidesData = { ...slidesData, [activeSlide]: currentValues };
    const nextSlideCount = slideCount + 1;

    updatedSlidesData[nextSlideCount] = getDefaultSlideValues(itemDefinitions);
    dispatchEditor({
      type: 'patch',
      patch: {
        slidesData: updatedSlidesData,
        slideCount: nextSlideCount,
        activeSlide: nextSlideCount
      }
    });
    form.reset(updatedSlidesData[nextSlideCount]);
  };

  /** Réinitialise la slide courante avec les valeurs mémorisées dans l’éditeur. */
  const handleResetCurrentSlide = () => {
    const storedValues = slidesData[activeSlide] ?? getDefaultSlideValues(itemDefinitions);
    form.reset(storedValues);
  };

  /** Calcule slides réindexées et slide active après suppression d’une slide. */
  const getSlideRemovalState = (slideIndex: number) => {
    const currentValues = form.getValues();
    const updatedSlidesData = { ...slidesData, [activeSlide]: currentValues };
    const reindexedSlides = reindexSlidesAfterRemoval(updatedSlidesData, slideCount, slideIndex);
    const nextSlideCount = slideCount - 1;
    let nextActiveSlide = activeSlide;

    if (slideIndex < activeSlide) {
      nextActiveSlide = activeSlide - 1;
    } else if (slideIndex === activeSlide) {
      nextActiveSlide = Math.min(slideIndex, nextSlideCount);
    }

    return {
      reindexedSlides,
      nextSlideCount,
      nextActiveSlide
    };
  };

  /** Supprime une slide en local et réindexe les données des slides restantes. */
  const handleRemoveSlide = (slideIndex: number) => {
    if (slideCount <= 1 || itemDefinitions.length === 0) return;

    const { reindexedSlides, nextSlideCount, nextActiveSlide } = getSlideRemovalState(slideIndex);

    dispatchEditor({
      type: 'patch',
      patch: {
        slidesData: reindexedSlides,
        slideCount: nextSlideCount,
        activeSlide: nextActiveSlide
      }
    });
    form.reset(reindexedSlides[nextActiveSlide] ?? getDefaultSlideValues(itemDefinitions));
  };

  /** Ouvre la confirmation de suppression d’une slide. */
  const handleRequestRemoveSlide = (slideIndex: number) => {
    if (slideCount <= 1 || itemDefinitions.length === 0) return;

    setPendingSlideRemoval(slideIndex);
  };

  /** Supprime une slide côté API (bloc existant) ou uniquement en local (mode création). */
  const handleConfirmRemoveSlide = async () => {
    if (pendingSlideRemoval === null) return;

    if (isCreatingBlock || !selectedWorkspace?.id) {
      handleRemoveSlide(pendingSlideRemoval);
      setPendingSlideRemoval(null);
      return;
    }

    const workspaceId = selectedWorkspace.id;
    const existingConfigurations = selectedWorkspace.widgetItemConfigurations;
    const slideIndexToRemove = pendingSlideRemoval;

    setIsSlideRemovalSaving(true);
    const { reindexedSlides, nextSlideCount, nextActiveSlide } =
      getSlideRemovalState(slideIndexToRemove);

    try {
      const saved = await saveSlideConfigurations(
        workspaceId,
        reindexedSlides,
        existingConfigurations,
        nextSlideCount
      );

      if (!saved) return;

      const nextActiveSlideValues =
        reindexedSlides[nextActiveSlide] ?? getDefaultSlideValues(itemDefinitions);

      form.reset(nextActiveSlideValues);
      dispatchEditor({
        type: 'patch',
        patch: {
          slidesData: reindexedSlides,
          slideCount: nextSlideCount,
          activeSlide: nextActiveSlide,
          savedSlidesSnapshot: createSlidesSnapshot(
            nextSlideCount,
            reindexedSlides,
            nextActiveSlide,
            nextActiveSlideValues,
            itemDefinitions
          )
        }
      });
      setPendingSlideRemoval(null);
      toast.success(t('admin.contentblock.slide.delete.success'));

      await queryClient.invalidateQueries({
        queryKey: getApiWidgetWorkspacesGetCollectionQueryKey()
      });
    } catch (error) {
      console.error(t('admin.contentblock.slide.delete.error'), error);
      showOrvalErrorToast(error, t('admin.contentblock.slide.delete.error'));
    } finally {
      setIsSlideRemovalSaving(false);
    }
  };

  /**
   * Prépare une slide pour le PATCH : conserve les IRI média existantes,
   * upload si un fichier est présent, normalise les couleurs HSL.
   */
  const processSlideValues = async (values: SlideFormValues): Promise<SlideFormValues | null> => {
    const processedValues = { ...values };
    const itemDefinitionByKey = new Map(
      itemDefinitions.map((itemDef) => [itemDef.itemKey, itemDef])
    );

    for (const [key, value] of Object.entries(values)) {
      const itemDef = itemDefinitionByKey.get(key);
      if (!itemDef) continue;

      if (isMediaItemDefinition(itemDef)) {
        if (typeof value === 'string' && value.trim().length > 0) {
          processedValues[key] = value;
        }
        continue;
      }

      if (itemDef.itemType === 'hsl_color' && typeof value === 'string' && value) {
        processedValues[key] = cleanHSLString(value);
      }
    }

    const mediaUploadTasks = Object.entries(values).flatMap(([key, value]) => {
      const itemDef = itemDefinitionByKey.get(key);
      if (!itemDef || !isMediaItemDefinition(itemDef)) return [];
      if (typeof value === 'string' && value.trim().length > 0) return [];

      const file = Array.isArray(value) ? value[0] : value instanceof File ? value : null;
      if (!file) return [];

      return [{ key, file }];
    });

    try {
      await Promise.all(
        mediaUploadTasks.map(async ({ key, file }) => {
          const media = await apiMediasPost({
            file,
            type: 'assets'
          });
          processedValues[key] = media.iri;
          const mediaId = getMediaIdFromIri(media.iri);
          if (mediaId) {
            queryClient.setQueryData(getApiMediasIdGetQueryKey(mediaId), media);
          }
        })
      );
    } catch (error) {
      const failedKey = mediaUploadTasks[0]?.key ?? 'media';
      console.error(t('admin.contentblock.form.upload.error', { field: failedKey }), error);
      showOrvalErrorToast(error, t('admin.contentblock.form.upload.error', { field: failedKey }));
      return null;
    }

    return normalizeSlideSelectValues(processedValues, itemDefinitions);
  };

  /** Upload immédiat d’un fichier média ; le formulaire conserve uniquement son IRI. */
  const handleMediaUpload = async (
    itemKey: string,
    files: File[],
    onChange: (value: string) => void
  ) => {
    if (files.length === 0) {
      onChange('');
      dispatchEditor({
        type: 'patch',
        patch: (previous) => ({
          slidesData: {
            ...previous.slidesData,
            [activeSlide]: {
              ...(previous.slidesData[activeSlide] ?? {}),
              [itemKey]: ''
            }
          }
        })
      });
      return;
    }

    setIsMediaUploading(true);
    try {
      const media = await apiMediasPost({
        file: files[0],
        type: 'assets'
      });
      onChange(media.iri);
      const mediaId = getMediaIdFromIri(media.iri);
      if (mediaId) {
        queryClient.setQueryData(getApiMediasIdGetQueryKey(mediaId), media);
      }
      dispatchEditor({
        type: 'patch',
        patch: (previous) => ({
          slidesData: {
            ...previous.slidesData,
            [activeSlide]: {
              ...(previous.slidesData[activeSlide] ?? {}),
              [itemKey]: media.iri
            }
          }
        })
      });
    } catch (error) {
      console.error(t('admin.contentblock.form.upload.error', { field: itemKey }), error);
      showOrvalErrorToast(error, t('admin.contentblock.form.upload.error', { field: itemKey }));
    } finally {
      setIsMediaUploading(false);
    }
  };

  /**
   * Envoie les configurations de chaque slide au workspace (PATCH),
   * puis vide les slides supprimées côté serveur.
   */
  const saveSlideConfigurations = async (
    workspaceId: number,
    allSlidesData: Record<number, SlideFormValues>,
    existingConfigurations?: WidgetWorkspaceWidgetWorkspaceReadModel['widgetItemConfigurations'],
    savedSlideCount: number = slideCount
  ) => {
    const slideIndexes = Array.from({ length: savedSlideCount }, (_, index) => index + 1).filter(
      (slideIndex) => allSlidesData[slideIndex]
    );

    const preparedSlides = await Promise.all(
      slideIndexes.map(async (slideIndex) => {
        const slideValues = allSlidesData[slideIndex];
        if (!slideValues) {
          return null;
        }
        const processed = await processSlideValues(slideValues);
        if (!processed) {
          return null;
        }

        const patchConfigurations = buildPatchConfigurations(itemDefinitions, processed);
        if (patchConfigurations.length === 0) {
          return null;
        }

        const missingMediaItem = findMissingMediaIriInPatch(itemDefinitions, patchConfigurations);
        if (missingMediaItem) {
          return { missingMediaItem } as const;
        }

        return { slideIndex, patchConfigurations };
      })
    );

    for (const prepared of preparedSlides) {
      if (prepared !== null && 'missingMediaItem' in prepared && prepared.missingMediaItem) {
        showOrvalErrorToast(
          new Error('Missing media IRI for patch'),
          t('admin.contentblock.form.media.reupload', {
            field: prepared.missingMediaItem.itemLabel
          })
        );
        return false;
      }
    }

    const slidesToPatch = preparedSlides.filter(
      (
        prepared
      ): prepared is {
        slideIndex: number;
        patchConfigurations: ReturnType<typeof buildPatchConfigurations>;
      } => prepared !== null && !('missingMediaItem' in prepared)
    );

    await Promise.all(
      slidesToPatch.map(({ slideIndex, patchConfigurations }) =>
        apiAdminwidgetWorkspacesIdPatch(String(workspaceId), {
          slideIndex,
          patchConfigurations
        })
      )
    );

    const removedSlideIndexes = getRemovedSlideIndexes(savedSlideCount, existingConfigurations);

    await Promise.all(
      removedSlideIndexes.map((slideIndex) =>
        apiAdminwidgetWorkspacesIdPatch(String(workspaceId), {
          slideIndex,
          patchConfigurations: []
        })
      )
    );

    return true;
  };

  /**
   * Soumission du formulaire : crée le workspace si nécessaire (inactif puis actif),
   * fusionne les slides en mémoire et persiste toutes les configurations.
   */
  const onSubmit = async (_values: SlideFormValues) => {
    const widgetId = currentWidget?.id;
    if (!widgetId || itemDefinitions.length === 0) return;

    const missingDefinitionIds = itemDefinitions.filter(
      (itemDef) => !getWidgetItemDefinitionIri(itemDef)
    );
    if (missingDefinitionIds.length > 0) {
      showOrvalErrorToast(
        new Error('Missing widget item definition ids'),
        t('admin.contentblock.form.error')
      );
      return;
    }

    const values = form.getValues();
    const previousSlideData = slidesData[activeSlide] ?? {};
    let mergedActiveSlide: SlideFormValues = normalizeSlideSelectValues(
      { ...previousSlideData, ...values },
      itemDefinitions
    );

    for (const itemDef of itemDefinitions) {
      if (!isMediaItemDefinition(itemDef)) continue;

      const key = itemDef.itemKey;
      if (
        !isMediaFormValueFilled(mergedActiveSlide[key]) &&
        isMediaFormValueFilled(previousSlideData[key])
      ) {
        mergedActiveSlide[key] = previousSlideData[key];
      }
    }

    mergedActiveSlide = normalizeSlideSelectValues(mergedActiveSlide, itemDefinitions);

    const allSlidesData = { ...slidesData, [activeSlide]: mergedActiveSlide };
    dispatchEditor({ type: 'patch', patch: { slidesData: allSlidesData } });

    if (!isScheduleRangeValid(schedule)) {
      toast.error(t('admin.contentblock.schedule.invalid_range'));
      return;
    }

    const schedulePayload = toApiSchedulePayload(schedule);

    try {
      let workspaceId = selectedWorkspace?.id;
      const isNewWorkspace = isCreatingBlock || workspaceId === undefined;

      if (isNewWorkspace) {
        // POST validates mandatory widgetItemConfigurations when isActive is true.
        // Create inactive first, PATCH slide configs, then activate.
        const sameTypeCount = pageBlocks.filter((block) => block.widget.id === widgetId).length;
        const workspaceName =
          sameTypeCount === 0
            ? currentWidget.label
            : `${currentWidget.label} (${sameTypeCount + 1})`;

        const createdWorkspace = await apiAdminwidgetWorkspacesPost({
          name: workspaceName,
          isActive: false,
          widget: `/widgets/${widgetId}`,
          sortOrder: getNextSortOrderForPage(pageBlocks),
          ...schedulePayload
        });
        workspaceId = createdWorkspace.id;
      }

      if (!workspaceId) return;

      if (!isNewWorkspace) {
        await apiAdminwidgetWorkspacesIdPatch(String(workspaceId), schedulePayload);
      }

      const saved = await saveSlideConfigurations(
        workspaceId,
        allSlidesData,
        selectedWorkspace?.widgetItemConfigurations,
        slideCount
      );
      if (!saved) return;

      if (isNewWorkspace) {
        await apiAdminwidgetWorkspacesIdPatch(String(workspaceId), {
          isActive: true
        });
      }

      dispatchEditor({
        type: 'patch',
        patch: {
          savedSlidesSnapshot: createSlidesSnapshot(
            slideCount,
            allSlidesData,
            activeSlide,
            mergedActiveSlide,
            itemDefinitions
          ),
          savedSchedulingSnapshot: createSchedulingSnapshot(schedule)
        }
      });
      toast.success(t('admin.contentblock.form.success'));

      await queryClient.invalidateQueries({
        queryKey: getApiWidgetWorkspacesGetCollectionQueryKey()
      });

      if (isNewWorkspace && workspaceId) {
        void navigate({
          to: '/content/content-block/$workspaceId',
          params: { workspaceId: String(workspaceId) },
          replace: true
        });
      }
    } catch (error) {
      console.error(t('admin.contentblock.form.error'), error);
      showOrvalErrorToast(error, t('admin.contentblock.form.error'));
    }
  };

  let loadState: ContentBlockEditorLoadState = 'ready';
  if (widgetsQuery.isLoading || widgetWorkspacesQuery.isLoading) {
    loadState = 'loading';
  } else if (widgetsQuery.isError || widgetWorkspacesQuery.isError) {
    loadState = 'error';
  } else if (
    mode === 'edit' &&
    widgetWorkspacesQuery.isSuccess &&
    selectedWorkspace === undefined
  ) {
    loadState = 'not_found';
  }

  return {
    loadState,
    t,
    navigate,
    mode,
    selectedWorkspace,
    form,
    formSchema,
    itemDefinitions,
    previewWidgetSlides,
    activeSlide,
    slideCount,
    progresses,
    uploadState: { isUploading, isMediaUploading },
    schedule,
    handleScheduleChange,
    handleBackToHub,
    handleSlideChange,
    handleAddSlide,
    handleResetCurrentSlide,
    handleRequestRemoveSlide,
    onSubmit,
    handleMediaUpload,
    dialogFlags: { isUnsavedWarningOpen, isSlideRemovalSaving },
    handleUnsavedWarningOpenChange,
    handleConfirmLeave,
    pendingSlideRemoval,
    handleSlideRemovalOpenChange,
    preventDialogDismissWhileSaving,
    handleConfirmRemoveSlide
  };
}
